import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useAuth } from "../context/AuthContext";
import { getSalonTimezone } from "../utils/salonTimezone";
import { listStaff } from "../api/staffs";
import {
  getTicketSummaryReport,
  exportTicketSummaryCsv,
} from "../api/reports";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import InfoTooltip from "../components/common/InfoTooltip";
import DateRangeErrorBanner from "../components/common/DateRangeErrorBanner";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";
import { REPORT_HELP } from "../constants/reportHelpContent";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import "./TicketSummaryReport.css";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

const DATE_FORMAT = "YYYY-MM-DD";

const QUICK_PICK_OPTIONS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "this_week", label: "This Week" },
  { key: "last_week", label: "Last Week" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
];

const QUICK_PICK_BASE =
  "h-9 px-4 rounded-full text-sm font-semibold transition";
const QUICK_PICK_ACTIVE = "bg-rose-500 text-white";
const QUICK_PICK_INACTIVE =
  "border border-slate-300 text-slate-700 hover:border-slate-400";

const TOOLTIP_TEXT = {
  start_date: "First day to include (uses salon timezone).",
  end_date: "Last day to include (uses salon timezone).",
  employee:
    "Filter tickets by technician/employee. Choose ALL to include everyone.",
  quick_picks: "One-click date ranges (based on salon timezone).",
  detail_view: "Shows each ticket and line item breakdown.",
  summary_view: "Shows totals grouped by employee for the selected period.",
  export_csv: "Download the current view as CSV.",
  run_report: "Refresh results using the selected filters.",
};

const parseSalonDate = (value, timeZone) => {
  if (!value || !timeZone) return null;
  const parsed = dayjs.tz(value, DATE_FORMAT, timeZone);
  return parsed.isValid() ? parsed : null;
};

const buildQuickPickRange = ({ pick, timeZone, salonTodayIso }) => {
  if (!timeZone || !pick) return null;
  const base =
    parseSalonDate(salonTodayIso, timeZone) || dayjs().tz(timeZone);
  if (!base?.isValid?.()) return null;

  let start = base;
  let end = base;

  if (pick === "yesterday") {
    start = base.subtract(1, "day");
    end = start;
  } else if (pick === "this_week") {
    start = base.startOf("isoWeek");
    end = base.endOf("isoWeek");
  } else if (pick === "last_week") {
    start = base.subtract(1, "week").startOf("isoWeek");
    end = start.endOf("isoWeek");
  } else if (pick === "this_month") {
    start = base.startOf("month");
    end = base.endOf("month");
  } else if (pick === "last_month") {
    start = base.subtract(1, "month").startOf("month");
    end = start.endOf("month");
  }

  return {
    start: start.format(DATE_FORMAT),
    end: end.format(DATE_FORMAT),
  };
};

const formatMoney = (value) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "$0.00";
  const fixed = Math.abs(num).toFixed(2);
  return num < 0 ? `-$${fixed}` : `$${fixed}`;
};

const isInvalidDateRange = (startValue, endValue) => {
  if (!startValue || !endValue) return false;
  return endValue < startValue;
};

const TOTAL_CARD =
  "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm";

export default function TicketSummaryReport() {
  const { currentSalon } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.TICKET_SUMMARY;
  const salonTimezone = useMemo(
    () => getSalonTimezone(currentSalon) || "UTC",
    [currentSalon]
  );
  const salonTodayIso = useMemo(() => {
    const base = dayjs().tz(salonTimezone);
    return base.isValid() ? base.format(DATE_FORMAT) : "";
  }, [salonTimezone]);

  const defaultRange = useMemo(() => {
    const base = parseSalonDate(salonTodayIso, salonTimezone);
    if (!base) return { start: "", end: "" };
    return {
      start: base.startOf("month").format(DATE_FORMAT),
      end: base.format(DATE_FORMAT),
    };
  }, [salonTodayIso, salonTimezone]);

  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [employeeId, setEmployeeId] = useState("");
  const [viewMode, setViewMode] = useState("detail");
  const [quickPick, setQuickPick] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dateRangeError, setDateRangeError] = useState("");
  const [staffOptions, setStaffOptions] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const runReportRef = useRef(null);

  useEffect(() => {
    if (defaultRange.start && !startDate) {
      setStartDate(defaultRange.start);
    }
    if (defaultRange.end && !endDate) {
      setEndDate(defaultRange.end);
    }
  }, [defaultRange, startDate, endDate]);

  useEffect(() => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
    } else {
      setDateRangeError("");
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!quickPick) return;
    const range = buildQuickPickRange({
      pick: quickPick,
      timeZone: salonTimezone,
      salonTodayIso,
    });
    if (!range) return;
    if (range.start !== startDate || range.end !== endDate) {
      setQuickPick(null);
    }
  }, [quickPick, salonTimezone, salonTodayIso, startDate, endDate]);

  useEffect(() => {
    let mounted = true;
    const loadStaff = async () => {
      setStaffLoading(true);
      try {
        const data = await listStaff({ salon_id: currentSalon?.id });
        if (mounted) {
          setStaffOptions(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (mounted) {
          setStaffOptions([]);
        }
      } finally {
        if (mounted) {
          setStaffLoading(false);
        }
      }
    };
    if (currentSalon?.id) {
      loadStaff();
    }
    return () => {
      mounted = false;
    };
  }, [currentSalon?.id]);

  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={TOOLTIP_TEXT[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const handleQuickPick = (pickKey) => {
    const range = buildQuickPickRange({
      pick: pickKey,
      timeZone: salonTimezone,
      salonTodayIso,
    });
    if (!range) return;
    setQuickPick(pickKey);
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const runReport = async () => {
    if (!startDate || !endDate || isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
        employee_id: employeeId ? Number(employeeId) : null,
        view: viewMode,
      };
      const data = await getTicketSummaryReport(params);
      setReport(data);
    } catch (err) {
      setError("Unable to load ticket summary report.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };
  runReportRef.current = runReport;

  useEffect(() => {
    const handleSalonMoved = () => {
      if (runReportRef.current) {
        runReportRef.current();
      }
    };
    window.addEventListener("sbin:salon-moved", handleSalonMoved);
    return () => {
      window.removeEventListener("sbin:salon-moved", handleSalonMoved);
    };
  }, []);

  const handleExport = async () => {
    if (!startDate || !endDate || isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
      return;
    }
    try {
      await exportTicketSummaryCsv({
        start_date: startDate,
        end_date: endDate,
        employee_id: employeeId ? Number(employeeId) : null,
        view: viewMode,
      });
    } catch (err) {
      setError("Unable to export ticket summary.");
    }
  };

  const employees = report?.employees || [];
  const totals = report?.grand_totals || {};

  return (
    <div className="ticketSummaryPageShell min-h-screen bg-slate-100">
      <div className="ticketSummaryPageWide ticket-summary-page-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Ticket Summary" />
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div id="ticket-summary-overview" className={getSectionClassName("ticket-summary-overview")}>
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                storageKey="report_help_ticket_summary"
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id="ticket-summary-filters"
              className={`rounded-2xl border border-slate-200 bg-slate-50 p-4 md:p-6 space-y-4 ${getSectionClassName(
                "ticket-summary-filters"
              )}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setViewMode("detail")}
                      className={`${BTN_OUTLINE} ${
                        viewMode === "detail"
                          ? "border-rose-500 text-rose-600"
                          : ""
                      }`}
                    >
                      Detail Report
                    </button>
                    <InfoTooltip
                      text={TOOLTIP_TEXT.detail_view}
                      label="Detail Report"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setViewMode("summary")}
                      className={`${BTN_OUTLINE} ${
                        viewMode === "summary"
                          ? "border-rose-500 text-rose-600"
                          : ""
                      }`}
                    >
                      Summary Report
                    </button>
                    <InfoTooltip
                      text={TOOLTIP_TEXT.summary_view}
                      label="Summary Report"
                    />
                  </div>
                </div>
                <div
                  id="ticket-summary-export"
                  className={`flex items-center gap-2 ${getSectionClassName(
                    "ticket-summary-export"
                  )}`}
                >
                  <button
                    type="button"
                    onClick={handleExport}
                    className={BTN_OUTLINE}
                    disabled={loading}
                  >
                    Export CSV
                  </button>
                  <InfoTooltip text={TOOLTIP_TEXT.export_csv} label="Export CSV" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  {renderFieldLabel("Start Date", "start_date")}
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("End Date", "end_date")}
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Employee", "employee")}
                  <select
                    value={employeeId}
                    onChange={(event) => setEmployeeId(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    <option value="">All</option>
                    {staffOptions.map((staff) => (
                      <option key={staff.id} value={staff.id}>
                        {staff.display_name}
                      </option>
                    ))}
                  </select>
                  {staffLoading && (
                    <div className="text-xs text-slate-500 mt-1">
                      Loading employees...
                    </div>
                  )}
                </div>
                <div>
                  {renderFieldLabel("Quick Pick Dates", "quick_picks")}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {QUICK_PICK_OPTIONS.map((option) => {
                      const isActive = quickPick === option.key;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => handleQuickPick(option.key)}
                          className={`${QUICK_PICK_BASE} ${
                            isActive ? QUICK_PICK_ACTIVE : QUICK_PICK_INACTIVE
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-500">
                  Salon timezone: {report?.filters?.timezone || salonTimezone || "--"}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={runReport}
                    className={BTN_PRIMARY}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Run Report"}
                  </button>
                  <InfoTooltip text={TOOLTIP_TEXT.run_report} label="Run Report" />
                </div>
              </div>
            </div>

            {dateRangeError && <DateRangeErrorBanner message={dateRangeError} />}
            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}

            {report && (
              <div
                id="ticket-summary-kpis"
                className={`grid grid-cols-1 md:grid-cols-5 gap-3 ${getSectionClassName(
                  "ticket-summary-kpis"
                )}`}
              >
                {[
                  { label: "Services", value: totals.services },
                  { label: "Retail", value: totals.retail },
                  { label: "Tips", value: totals.tips },
                  { label: "Taxes", value: totals.taxes },
                  { label: "Total", value: totals.total },
                ].map((entry) => (
                  <div key={entry.label} className={TOTAL_CARD}>
                    <div className="text-xs text-slate-500 uppercase tracking-wide">
                      {entry.label}
                    </div>
                    <div className="text-lg font-semibold text-slate-900">
                      {formatMoney(entry.value)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div
              id="ticket-summary-details"
              className={getSectionClassName("ticket-summary-details")}
            >
              {report && employees.length === 0 && !loading && (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-600">
                  No ticket data found for this range.
                </div>
              )}

              {employees.map((employee) => (
                <div
                  key={employee.employee_id ?? employee.employee_name}
                  className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">
                        {employee.employee_name || "Unassigned"}
                      </h3>
                      <div className="text-xs text-slate-500">
                        Employee totals for selected range
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm text-slate-600">
                      <span>Services: {formatMoney(employee.employee_totals?.services)}</span>
                      <span>Retail: {formatMoney(employee.employee_totals?.retail)}</span>
                      <span>Tips: {formatMoney(employee.employee_totals?.tips)}</span>
                      <span>Taxes: {formatMoney(employee.employee_totals?.taxes)}</span>
                      <span>Total: {formatMoney(employee.employee_totals?.total)}</span>
                    </div>
                  </div>

                  {viewMode === "summary" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="text-sm font-semibold text-slate-700">
                          Forms of Payment
                        </div>
                        <div className="mt-2 space-y-1 text-sm text-slate-600">
                          {(employee.payments_summary || []).length ? (
                            employee.payments_summary.map((payment) => (
                              <div
                                key={`${payment.method}-${payment.amount}`}
                                className="flex items-center justify-between"
                              >
                                <span>{payment.method}</span>
                                <span>{formatMoney(payment.amount)}</span>
                              </div>
                            ))
                          ) : (
                            <div>No payments found.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {viewMode === "detail" &&
                    (employee.tickets || []).map((ticket) => (
                      <div
                        key={ticket.ticket_id}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-4"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="font-semibold text-slate-800">
                            Ticket: {ticket.ticket_date || "--"} for{" "}
                            {ticket.client_name || "Walk-in"}
                          </div>
                          <div className="text-xs text-slate-500">
                            Ticket #{ticket.ticket_number || "--"}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                          {[
                            { label: "Services", value: ticket.totals?.services },
                            { label: "Retail", value: ticket.totals?.retail },
                            { label: "Tips", value: ticket.totals?.tips },
                            { label: "Taxes", value: ticket.totals?.taxes },
                            { label: "Total", value: ticket.totals?.total },
                          ].map((entry) => (
                            <div key={entry.label} className="rounded-xl bg-white p-3">
                              <div className="text-[11px] text-slate-500 uppercase">
                                {entry.label}
                              </div>
                              <div className="text-sm font-semibold text-slate-900">
                                {formatMoney(entry.value)}
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="rounded-xl border border-slate-200 bg-white">
                            <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                              Details
                            </div>
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="text-left text-xs text-slate-500">
                                  <th className="px-3 py-2">Item</th>
                                  <th className="px-3 py-2">Qty</th>
                                  <th className="px-3 py-2 text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(ticket.details || []).length ? (
                                  ticket.details.map((detail, idx) => (
                                    <tr
                                      key={`${detail.name}-${idx}`}
                                      className="border-t border-slate-100 text-slate-700"
                                    >
                                      <td className="px-3 py-2">
                                        {detail.name || "--"}
                                      </td>
                                      <td className="px-3 py-2">
                                        {detail.quantity}
                                      </td>
                                      <td className="px-3 py-2 text-right">
                                        {formatMoney(detail.amount)}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td
                                      colSpan={3}
                                      className="px-3 py-3 text-slate-500"
                                    >
                                      No line items.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          <div className="rounded-xl border border-slate-200 bg-white">
                            <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">
                              Forms of Payment
                            </div>
                            <div className="p-3 space-y-2 text-sm text-slate-700">
                              {(ticket.payments || []).length ? (
                                ticket.payments.map((payment, idx) => (
                                  <div
                                    key={`${payment.method}-${idx}`}
                                    className="flex items-center justify-between"
                                  >
                                    <span>{payment.method}</span>
                                    <span>{formatMoney(payment.amount)}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="text-slate-500">
                                  No payments found.
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
